import type { SortDirection } from '@/types';

import type { MeetingData } from '../../types';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { Typography } from 'antd';

import themeToken from '@lib/theme/tokens/index';
import { StyledButton } from '@/lib/theme/components/Button';
import { PageWrapper } from '@/components/Shared/Wrapper';
import PlusIcon from '@/components/Icons/PlusIcon';
import CommonTable from '@/components/Shared/CommonTable';
import HeaderWrapper from '@/components/Shared/HeaderWrapper';
import { useSelectedCompanyStore } from '@/stores/selectedCompany';
import { usePermissionsStore } from '@/stores/Permissions';
import { useTrackEvent } from '@/hooks/useTrackEvents';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import MainContainer from '@/components/Shared/MainContainer';
import VideoIcon from '@/components/Icons/VideoIcon';
import CommonModal from '@/features/captable/components/Shared/CommonModal';
import SharedModal from '@/components/Shared/SharedModal';

import { meetingsColumns } from '../../utils/meetingsColumns';
import { useGetMeetings } from '../../api/meetings/getMeetings';
import { useGetMeetingTypes } from '../../api/meetings/getMeetingTypes';
import MeetingForm from '../../components/MeetingForm';
import { useDeleteMeeting } from '../../api/meetings/deleteMeeting';
import { LimitsCheckerContext } from '@/components/LimitsChecker';

const { Title } = Typography;

const Meetings = () => {
  const { sendMixpanelEvent } = useTrackEvent({ page: 'meetings-list' });
  const { recheckLimits, disableRecordsAddition } = useOutletContext<LimitsCheckerContext>();

  const { permissions } = usePermissionsStore((state) => state);
  const { t } = useTranslation('investorRelations', {
    keyPrefix: 'meetings',
  });

  const { t: translate } = useTranslation('pageTitles', {
    keyPrefix: 'investorRelations',
  });

  useDocumentTitle(translate('meetings'));

  const { selectedCompany } = useSelectedCompanyStore((state) => state);

  const [searchToken, setSearchToken] = useState<string>();
  const [sortParams, setSortParams] = useState<{
    sort_by: keyof MeetingData;
    sort_dir: SortDirection;
  }>();
  const [paginationParams, setPaginationParams] = useState({
    pageSize: 10,
    current: 1,
    total: 0,
  });
  const { data: meetingsList, isLoading: isGetMeetingsLoading } = useGetMeetings({
    companyID: selectedCompany?.cid as number,
    queryParams: {
      ...sortParams,
      q: searchToken,
      page: paginationParams.current,
      limit: paginationParams.pageSize,
    },
  });

  const [isMeetingModalOpen, setIsMeetingModalOpen] = useState<boolean>(false);
  const [isDeleteMeetingModalOpen, setIsDeleteMeetingModalOpen] = useState<boolean>(false);
  const [selectedMeetingId, setSelectedMeetingId] = useState<number>();
  const [selectedMeetingName, setSelectedMeetingName] = useState<string>();

  const navigate = useNavigate();

  const { data: meetingTypes, isLoading: isGetMeetingTypesLoading } = useGetMeetingTypes({
    companyID: selectedCompany?.cid as number,
  });

  const { mutate: mutateDeleteMeeting, isPending: isDeleteMeetingPending } = useDeleteMeeting({
    postSuccess: () => {
      const { pageSize, current, total } = paginationParams;
      const updatedItemsTotal = total - 1;
      const updatedTotalPages = Math.ceil(updatedItemsTotal / pageSize) || 1;

      if (updatedTotalPages < current) setPaginationParams((prev) => ({ ...prev, current: prev.current - 1 }));
      recheckLimits();
    },
  });

  const isLoading = useMemo(
    () => isGetMeetingsLoading || isGetMeetingTypesLoading,
    [isGetMeetingsLoading, isGetMeetingTypesLoading],
  );
  const canCreateMeeting = useMemo(() => permissions.includes('company.board.MOM'), [permissions]);
  const canDeleteMeeting = useMemo(() => permissions.includes('company.board.MOM'), [permissions]);

  const deleteMeetingHandler = useCallback(
    (meetingID: number) => {
      mutateDeleteMeeting({ companyID: selectedCompany?.cid as number, meetingID });
    },
    [selectedCompany?.cid, mutateDeleteMeeting],
  );

  const handleDeleteMeetingModalOpen = useCallback(
    (meetingID: number, meetingName: string) => {
      sendMixpanelEvent('delete-meeting-btn-clicked');
      setSelectedMeetingId(meetingID);
      setSelectedMeetingName(meetingName);

      setIsDeleteMeetingModalOpen(true);
    },
    [setIsDeleteMeetingModalOpen, sendMixpanelEvent],
  );

  const handleDeleteMeetingModalCancel = useCallback(() => {
    sendMixpanelEvent('delete-meeting-modal-cancelled');
    setSelectedMeetingId(undefined);
    setSelectedMeetingName(undefined);

    setIsDeleteMeetingModalOpen(false);
  }, [setIsDeleteMeetingModalOpen, sendMixpanelEvent]);

  const handleMeetingModalOpen = useCallback(
    (meetingID?: number) => {
      if (meetingID) {
        sendMixpanelEvent('edit-meeting-btn-clicked');
        setSelectedMeetingId(meetingID);
      } else {
        setSelectedMeetingId(undefined);
        sendMixpanelEvent('add-new-meeting-btn-clicked');
      }

      setIsMeetingModalOpen(true);
    },
    [setIsMeetingModalOpen, sendMixpanelEvent],
  );

  const handleMeetingModalCancel = useCallback(() => {
    setIsMeetingModalOpen(false);
  }, [setIsMeetingModalOpen]);

  const tableColumns = useMemo(
    () =>
      meetingsColumns(
        setSearchToken,
        handleMeetingModalOpen,
        handleDeleteMeetingModalOpen,
        isLoading,
        !canDeleteMeeting || isDeleteMeetingPending,
        meetingTypes?.data,
        selectedCompany?.is_owner,
        t,
      ),
    [
      setSearchToken,
      handleMeetingModalOpen,
      handleDeleteMeetingModalOpen,
      isLoading,
      canDeleteMeeting,
      isDeleteMeetingPending,
      meetingTypes?.data,
      selectedCompany?.is_owner,
      t,
    ],
  );

  useEffect(() => {
    if (meetingsList?.metadata) {
      const { total } = meetingsList.metadata;
      setPaginationParams((prev) => ({ ...prev, total }));
    }
  }, [meetingsList?.metadata]);

  useEffect(() => {
    if (searchToken) setSearchToken(searchToken);
  }, [searchToken]);

  return (
    <MainContainer
      title={t('investorRelations')}
      breadcrumbItems={[
        {
          title: (
            <>
              <VideoIcon width={12} height={20} active />
              {t('meetings')}
            </>
          ),
        },
      ]}
    >
      <PageWrapper>
        <div>
          <HeaderWrapper>
            <Title
              level={5}
              style={{
                color: themeToken['branding-primary-6'],
                fontWeight: 600,
                marginBottom: 0,
              }}
            >
              {t('myMeetings')}
            </Title>

            <StyledButton
              size="small"
              ghost
              type="primary"
              icon={<PlusIcon />}
              onClick={() => handleMeetingModalOpen()}
              disabled={!canCreateMeeting || disableRecordsAddition}
            >
              {t('newMeeting')}
            </StyledButton>
          </HeaderWrapper>
          <CommonTable
            columns={tableColumns}
            dataSource={meetingsList?.data}
            isLoading={isLoading}
            paginationParams={paginationParams}
            setPaginationParams={setPaginationParams}
            setSortParams={setSortParams}
            onRow={(record: MeetingData | Record<string, number>) => {
              return {
                style: {
                  cursor: 'pointer',
                },
                onClick: () => {
                  navigate(`/investor-relations/meetings/${record.id}`);
                },
              };
            }}
          />
        </div>
      </PageWrapper>
      <CommonModal isModalOpen={isMeetingModalOpen} handleCancel={handleMeetingModalCancel} destroyOnClose>
        <MeetingForm
          handleCancel={handleMeetingModalCancel}
          handleOk={handleMeetingModalCancel}
          selectedMeetingId={selectedMeetingId}
        />
      </CommonModal>
      <SharedModal
        open={isDeleteMeetingModalOpen}
        setConfirmDeleteOpen={handleDeleteMeetingModalCancel}
        onOk={() => deleteMeetingHandler(selectedMeetingId as number)}
        confirmationText={t('yesDelete')}
        title={t('deleteWarning')}
        content={
          <Typography.Text>
            {t('deleteConfirmation')} <span style={{ fontWeight: 'bold' }}> {selectedMeetingName as string} </span> ?{' '}
          </Typography.Text>
        }
      />
    </MainContainer>
  );
};

export default Meetings;
